// src/components/RosterOnFilePanel.js
//
// RULING 330 Part 1 (Bob 2026-10-23): the General Chairperson files a seniority roster ON FILE for his committee, so an
// application is cross-referenced against it at signup ("an automated mechanical means of verification"). A roster he files
// is CONFIRMED (his say-so); a public roster he loads is PROVISIONAL (unconfirmed). Officer-supplied rosters come through
// the attestation link (Part 3). Stored under the committee's wall - only he and the administrator read it (firestore.rules
// rosters); the Local Chairperson never reads the roster, only the match result that travels on the application.
//
// A provisional or officer-supplied match NEVER grants access by itself (the wall). This files the roster and re-checks the
// pending applications against it; the decision stays the Local Chairperson's (ruling 193).
import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ROSTER_STATUS } from '../data/rosterOnFile';

const C = { panel: '#0B1626', line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2' };

export default function RosterOnFilePanel({ user }) {
  const committee = (user && user.committee) || '';
  const isGC = !!(user && (user.role === 'general_chair' || user.isAdmin));
  const [rosters, setRosters] = useState(null);
  const [form, setForm] = useState({ craft: '', property: '', source: '', date: '', status: 'confirmed', rowsText: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!committee || !isGC) { setRosters(null); return undefined; }
    return onSnapshot(query(collection(db, 'rosters'), where('committee', '==', committee)),
      (s) => setRosters(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setRosters(null));
  }, [committee, isGC]);

  if (!isGC || !committee) return null;

  const parseRows = (t) => String(t || '').split('\n').map((l) => l.trim()).filter(Boolean).map((l) => {
    const parts = l.split(/[,\t]/).map((s) => s.trim());
    return { rosterNumber: parts[0] || '', seniorityDate: parts[1] || '', name: parts.slice(2).join(' ') };
  }).filter((r) => r.rosterNumber);

  const file = async () => {
    setBusy(true); setMsg(null);
    const rows = parseRows(form.rowsText);
    if (!rows.length) { setMsg({ ok: false, text: 'Add at least one row (roster number per line).' }); setBusy(false); return; }
    const sourceKind = form.status === 'confirmed' ? 'gc_item5' : 'public';
    try {
      const r = await httpsCallable(getFunctions(), 'putRosterOnFile')({ roster: {
        committee, craft: form.craft, property: form.property, source: form.source, date: form.date,
        status: form.status, sourceKind, rows } });
      setMsg({ ok: true, text: 'Roster on file (' + r.data.rows + ' rows). Re-check pending applications to apply it to the ones already waiting.' });
      setForm({ craft: '', property: '', source: '', date: '', status: 'confirmed', rowsText: '' });
    } catch (e) { setMsg({ ok: false, text: (e && e.message) || 'Not filed.' }); }
    setBusy(false);
  };
  const recheck = async () => {
    setBusy(true); setMsg(null);
    try { const r = await httpsCallable(getFunctions(), 'rematchApplications')({ committee }); setMsg({ ok: true, text: r.data.rechecked + ' pending application(s) re-checked against the rosters on file.' }); }
    catch (e) { setMsg({ ok: false, text: (e && e.message) || 'Not re-checked.' }); }
    setBusy(false);
  };

  return (
    <div style={{ border: '1px solid ' + C.line, background: C.panel, padding: '10px 14px', marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', cursor: 'pointer' }} onClick={() => setOpen((o) => !o)}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Seniority rosters on file{rosters ? ' (' + rosters.length + ')' : ''}</div>
        <div style={{ fontSize: 11, color: C.accent }}>{open ? 'Hide' : 'File a roster / re-check'}</div>
      </div>
      <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>An application is cross-referenced against these at signup. A roster you file is confirmed by you; a public roster is provisional. A match never grants access by itself - the Local Chairperson decides.</div>
      {open ? (
        <div style={{ marginTop: 10 }}>
          {rosters && rosters.length ? (
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 10px' }}>{rosters.slice().sort((a, b) => (a.date < b.date ? 1 : -1)).map((r) => (
              <li key={r.id} style={{ fontSize: 11, color: C.text, borderBottom: '1px dotted ' + C.line, padding: '3px 0' }}>
                {(r.craft || '?') + ' / ' + (r.property || '?') + ' - ' + ((r.rows && r.rows.length) || 0) + ' rows - ' + ((ROSTER_STATUS[r.status] && ROSTER_STATUS[r.status].words) || r.status) + (r.date ? ', dated ' + r.date : '')}
              </li>
            ))}</ul>
          ) : <div style={{ fontSize: 12, color: C.dim, marginBottom: 10 }}>No roster on file for this committee yet.</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <input aria-label="Craft and class" className="form-input" placeholder="Craft and class (exactly as on the application)" value={form.craft} onChange={(e) => setForm({ ...form, craft: e.target.value })} />
            <input aria-label="Railroad / property" className="form-input" placeholder="Railroad / property" value={form.property} onChange={(e) => setForm({ ...form, property: e.target.value })} />
            <input aria-label="Source (file name or URL)" className="form-input" placeholder="Source (file name or link)" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} />
            <input aria-label="Roster date" className="form-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} title="The date the roster is dated" />
            <select aria-label="Status" className="form-select" style={{ gridColumn: '1 / span 2' }} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="confirmed">Confirmed by me (the General Chairperson)</option>
              <option value="provisional">Provisional - a public roster, not confirmed</option>
            </select>
          </div>
          <textarea aria-label="Roster rows" className="form-textarea" style={{ width: '100%', marginTop: 8, minHeight: 90 }} placeholder={'One row per line: roster number, seniority date, name\n12345, 2014-03-01, J. Doe\n12346, 2015-07-16, A. Smith'} value={form.rowsText} onChange={(e) => setForm({ ...form, rowsText: e.target.value })} />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="btn btn-primary" disabled={busy} onClick={file}>File the roster</button>
            <button className="btn btn-secondary" disabled={busy} onClick={recheck}>Re-check pending applications</button>
          </div>
          {msg ? <div style={{ fontSize: 12, color: msg.ok ? C.ok : C.bad, marginTop: 8 }}>{msg.text}</div> : null}
        </div>
      ) : null}
    </div>
  );
}
